import { EventPreviewLayoutProps } from '../types';
import React from 'react';
import DOMPurify from 'dompurify';
import styles from './EventPreview.module.scss';

export default function EventPreviewLayoutWeb(props: EventPreviewLayoutProps) {
  const eventPreview = props.preview;
  const timezone = props.timezone;

  const formatDate = (date: Date | null) => {
    if (!date) return 'Not set';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'full',
    }).format(date);
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return 'Not set';
    const formatted = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'full',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(date);

    if (timezone) {
      const tzAbbr = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value;
      return `${formatted} ${tzAbbr || ''}`;
    }

    return formatted;
  };

  const formatAttendees = (attendees: Array<{ fullName: string | null; email: string }>) => {
    if (!attendees.length) return 'None';
    return attendees.map((attendee) =>
      attendee.fullName ? `${attendee.fullName} <${attendee.email}>` : attendee.email
    ).join(', ');
  };

  const formatOrganizer = (organizer: { fullName: string | null; email: string } | null) => {
    if (!organizer) return 'Not set';
    return organizer.fullName ? `${organizer.fullName} <${organizer.email}>` : organizer.email;
  };

  return (
    <>
      <span>
        Please review the appointment details below before saving:
      </span>
      <div className={styles.eventPreview}>
        <div className={styles.field}>
          <div className={styles.label}>Title:</div>
          <div className={styles.value}><b>{eventPreview.title}</b></div>
        </div>

        {eventPreview.organizer && (
          <div className={styles.field}>
            <div className={styles.label}>Organizer:</div>
            <div className={styles.value}>{formatOrganizer(eventPreview.organizer)}</div>
          </div>
        )}

        {eventPreview.location && (
          <div className={styles.field}>
            <div className={styles.label}>Location:</div>
            <div className={styles.value}>{eventPreview.location}</div>
          </div>
        )}

        {eventPreview.isAllDayEvent ? (
          <div className={styles.field}>
            <div className={styles.label}>Date:</div>
            <div className={styles.value}>{formatDate(eventPreview.startTime)} (All Day Event)</div>
          </div>
        ) : (
          <>
            <div className={styles.field}>
              <div className={styles.label}>Start Time:</div>
              <div className={styles.value}>{formatDateTime(eventPreview.startTime)}</div>
            </div>

            <div className={styles.field}>
              <div className={styles.label}>End Time:</div>
              <div className={styles.value}>{formatDateTime(eventPreview.endTime)}</div>
            </div>
          </>
        )}

        {eventPreview.attendees && eventPreview.attendees.length > 0 && (
          <div className={styles.field}>
            <div className={styles.label}>Attendees:</div>
            <div className={styles.value}>{formatAttendees(eventPreview.attendees)}</div>
          </div>
        )}

        {eventPreview.description && (
          <div className={styles.field}>
            <div className={styles.label}>Description:</div>
            <div
              className={styles.value}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(eventPreview.description) }}
            />
          </div>
        )}
      </div>
    </>
  );
}
